<template>
    <div class="bg-gray-50">
        <NotificationContainer />

        <main v-if="accountState.loaded" class="mb-8 mt-5 w-full space-y-8">
            <div class="grid max-w-3xl grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                <div class="space-y-6 lg:col-span-2 lg:col-start-1">
                    <section v-if="order">
                        <OrderHeader
                            :company-logo="order?.company_logo"
                            :asset-title="order.asset_data.asset_title"
                            :company-name="order.asset_data.company_name"
                            :amount="order.total_amount"
                            :currency="order.asset_data.currency" />
                    </section>
                    <div class="flex flex-wrap gap-4 md:flex-nowrap">
                        <aside class="w-full md:w-1/3">
                            <BaseAccordion
                                :title="`${$t('portfolio.orders.contract_documents')} (${contractDocuments?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList v-if="contractDocuments" :documents="contractDocuments" />
                            </BaseAccordion>
                            <BaseAccordion
                                :title="`${$t('portfolio.orders.reporting_documents')} (${reportingDocuments?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList v-if="reportingDocuments" :documents="reportingDocuments" />
                            </BaseAccordion>
                            <BaseAccordion
                                :title="`${$t('portfolio.orders.additional_documents')} (${attachments?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList v-if="attachments" :documents="attachments" />
                            </BaseAccordion>
                            <BaseAccordion
                                v-if="isCertificateEnabled()"
                                :title="`${$t('portfolio.orders.order_certificate')} (${certificate?.length})`"
                                class="border border-gray-300 bg-white">
                                <OrderDocumentsList v-if="certificate" :documents="certificate" />
                            </BaseAccordion>
                        </aside>
                        <div class="flex w-full flex-col lg:w-2/3">
                            <section>
                                <BaseSummary
                                    :title="$t('portfolio.orders.investment_details')"
                                    :items="investmentDetailsItems" />
                            </section>
                        </div>
                    </div>
                </div>

                <section class="lg:col-span-1 lg:col-start-3" aria-labelledby="user-profile">
                    <AccountSidePanel />
                </section>
            </div>
        </main>
        <div v-else class="flex h-[65vh] items-center justify-center">
            <LoadingSpinner class="h-10 w-10 fill-primary" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { i18n } from '../../localization'
import NotificationContainer from '../notification/NotificationContainer.vue'
import LoadingSpinner from '../base/LoadingSpinner.vue'
import { usePersonalAccountDataState } from '../../utilities/account/useAccountDataPersonal'
import AccountSidePanel from '../account/AccountSidePanel.vue'
import { isLegalProfile } from '../../utilities/investment-process/useUserProfileType'
import { useLegalAccountDataState } from '../../utilities/account/useAccountDataLegal'
import { ref, watchEffect } from 'vue'
import { OrderResponseObject, getOrder, showOrder } from '../../utilities/api'
import OrderHeader from './OrderHeader.vue'
import OrderDocumentsList from './OrderDocumentsList.vue'
import BaseAccordion from '../base/BaseAccordion.vue'
import BaseSummary, { BaseSummaryItem } from '../base/BaseSummary.vue'
import { OrderStatus } from '../../typings/enums'
import moment from 'moment'
import { getPlatformSettings, isCertificateEnabled } from '../../utilities/usePlatformSettings'

const props = defineProps<{
    orderId: string
}>()

const accountState = isLegalProfile() ? useLegalAccountDataState() : usePersonalAccountDataState()
const order = ref<OrderResponseObject | null>(null)

const contractDocuments = computed(() => {
    return order.value?.contract_data_documents.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

const reportingDocuments = computed(() => {
    return order.value?.reporting_documents.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

const attachments = computed(() => {
    return order.value?.attachments.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

const certificate = computed(() => {
    return order.value?.certificate.map((document) => {
        return {
            name: document.name,
            link: document.url,
        }
    })
})

function getOrderStatusColor(status: number) {
    if (status === OrderStatus['order_status_canceled']) {
        return 'text-gray-400'
    }
    return 'text-green-600'
}

const investmentDetailsItems = computed(() => {
    if (order.value) {
        const items: BaseSummaryItem[] = [
            {
                label: i18n.global.t('portfolio.orders.order_status'),
                value: i18n.global.t(
                    `portfolio.orders.order_status_${OrderStatus[order.value.status].toLocaleLowerCase()}`
                ),
                valueColor: getOrderStatusColor(order.value.status),
            },
            {
                label: i18n.global.t('portfolio.orders.investment_date'),
                value: moment(order.value.created_on).format('DD.MM.YYYY'),
            },
            {
                label: i18n.global.t('portfolio.orders.start_of_contract'),
                value: moment(order.value.start_of_contract).format('DD.MM.YYYY'),
            },
        ]

        return items
    }
    return []
})

watchEffect(async () => {
    await accountState.fetchAccountData()
    if (accountState.accountId) {
        const showOrderRequest = await showOrder(props.orderId)
        order.value = showOrderRequest.data.order
    }
})
</script>

<style scoped></style>
